/**
 * English: Menu and frame
 *
 * What this file covers: The side menu, its section headings, the top bar, the profile menu and the frame
 * around every page.
 *
 * Tone: One or two words per item, like a sign on a door. Nouns, not sentences. Every label must fit a narrow
 * menu. The words chosen here are the names of the pages everywhere else.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const menu: Catalog = {
  'nav.section.workspace': 'Workspace',
  'nav.section.audience': 'Audience',
  'nav.section.automation': 'Automation',
  'nav.section.settings': 'Settings',
  'nav.section.account': 'Account',
  'nav.dashboard': 'Dashboard',
  'nav.inbox': 'Inbox',
  'nav.contacts': 'Contacts',
  'nav.broadcasts': 'Broadcasts',
  'nav.templates': 'Templates',
  'nav.flows': 'Flows',
  'nav.numbers': 'Numbers',
  'nav.billing': 'Billing',
  'nav.settings': 'Settings',
  'nav.menu': 'Menu',
  'shell.org.tooltip': 'Your organisation. Open organisation settings.',
  'nav.openMenu': 'Open navigation',
  'nav.closeMenu': 'Close navigation',
  'shell.wallet.aria': 'Wallet balance {{amount}}. Open billing.',
  'shell.skipToContent': 'Skip to main content',
  'shell.loading': 'Loading your workspace…',
  'shell.session.expiredTitle': 'Session expired',
  'shell.session.expiredBody': 'You were signed out because your session expired. Sign in again to continue.',
  'shell.realtime.connected': 'Connected',
  'shell.realtime.connecting': 'Connecting…',
  'shell.realtime.disconnected': 'Offline',
  'shell.realtime.tooltip.connected': 'Live updates are on: new messages arrive automatically.',
  'shell.realtime.tooltip.connecting': 'Reconnecting to live updates…',
  'shell.realtime.tooltip.disconnected': 'Live updates are off. New messages will not appear until you reconnect.',
  'shell.realtime.reconnect': 'Reconnect',
  'shell.profile.open': 'Open account menu',
  'shell.profile.menu': 'Account menu',
  'shell.profile.guest': 'Signed out',
  'shell.profile.account': 'Account',
  'shell.profile.allSettings': 'All settings',
  'shell.localePicker': 'Change language',
  'shell.locale.partial': '{{label}} ({{percent}}% translated)',
  'shell.impersonation.title': 'Impersonation active',
  'shell.impersonation.body': 'You are viewing {{org}} as {{operator}}, from the platform console. This session ends automatically.',
  'shell.impersonation.exit': 'End impersonation',
  'shell.impersonation.exitAria': 'End impersonation and close this tab',
  'shell.error.title': 'Something went wrong on this page',
  'shell.error.body': 'An unexpected rendering error occurred.',
  'shell.error.retry': 'Try again',
  'shell.error.reload': 'Reload page',
  'shell.verifyEmail.title': 'Verify your email address',
  'shell.verifyEmail.body': 'Check {{email}} for the verification link. Some features stay locked until you confirm it.',
  'shell.verifyEmail.action': 'Resend link',
  'nav.knowledge': 'Knowledge base',
  'nav.compose': 'Compose',
  'nav.developers': 'Developers',
  'nav.channels': 'Channels',
  'nav.refer': 'Refer and earn',
  'nav.voice': 'Voice',
  'nav.section.bookings': 'Bookings',
  'nav.bookings.calendar': 'Calendar',
  'nav.bookings.list': 'Bookings',
  'nav.bookings.services': 'Services',
  'nav.bookings.staff': 'Staff and hours',
  'nav.bookings.classes': 'Classes',
  'nav.bookings.settings': 'Booking settings',
  'nav.section.tickets': 'Tickets',
  'nav.tickets.queue': 'Queue',
  'nav.tickets.board': 'Board',
  'nav.tickets.tasks': 'My tasks',
  'nav.tickets.reports': 'Reports',
  'nav.tickets.settings': 'Ticket settings',
  'shell.assistant.title': 'Assistant',
  'shell.assistant.subtitle': 'Help with the console, on any page',
  'shell.assistant.open': 'Ask the assistant',
  'shell.assistant.close': 'Close the assistant',
  'shell.assistant.clear': 'Start again',
  'shell.assistant.hello': 'Hi. Ask me anything about the console: what a page is for, where to find something, or how to do it.',
  'shell.assistant.helloName': 'Hi {{name}}. Ask me anything about the console: what a page is for, where to find something, or how to do it.',
  'shell.assistant.examples': 'For example: "What is this page for?", "Take me to my settings" or "Switch to dark mode". I always ask before I do anything.',
  'shell.assistant.placeholder': 'Ask a question or for something to be done',
  'shell.assistant.inputLabel': 'Your message to the assistant',
  'shell.assistant.send': 'Send',
  'shell.assistant.thinking': 'Thinking…',
  'shell.assistant.confirm': 'Yes, do it',
  'shell.assistant.decline': 'No thanks',
  'shell.assistant.actionDone': 'Done.',
  'shell.assistant.actionDeclined': 'Not done.',
  'shell.assistant.done': 'Done: {{action}}',
  'shell.assistant.failed': 'The assistant could not answer just now. Please try again.',
  'shell.assistant.noCredit': 'There is not enough credit to ask the assistant.',
  'shell.assistant.topUp': 'Top up',
  'shell.assistant.cost': 'Each message uses a little credit, charged as AI use.',
  'nav.aiTools': 'AI tools',
  'shell.assistant.hint': 'Ask me anything. I\'m here to help.',
  'shell.assistant.peekTitle': 'Hi, I\'m your assistant',
  'shell.assistant.peekTitleName': 'Hi {{name}}, I\'m your assistant',
  'shell.assistant.peekBody': 'Ask me anything. I can find a page, explain what you see or walk you through a task.',
  'shell.assistant.peekClose': 'Not now',
  'nav.insights': 'Insights'
};

export default menu;
